import { z } from 'zod';

const ConfigSchema = z.object({
  apiBaseUrl: z.string().url(),
  otelEndpoint: z.string().url(),
  grafanaUrl: z.string().url(),
  deployEnv: z.enum(['local', 'hosted']),
});

export type RuntimeConfig = z.infer<typeof ConfigSchema>;

export const LOCAL_DEFAULTS: RuntimeConfig = {
  apiBaseUrl: 'http://localhost',
  otelEndpoint: 'http://localhost:4318',
  grafanaUrl: 'http://localhost:3000',
  deployEnv: 'local',
};

let _config: RuntimeConfig | null = null;

/** Load runtime config once, validate with Zod; fall back to local defaults. */
export async function loadConfig(): Promise<RuntimeConfig> {
  if (_config) return _config;
  try {
    const res = await fetch('/runtime-config.json', { cache: 'no-store' });
    if (!res.ok) throw new Error(`config fetch ${res.status}`);
    const json = await res.json();
    _config = ConfigSchema.parse(json);
  } catch {
    _config = LOCAL_DEFAULTS;
  }
  return _config;
}

/** Synchronous accessor; throws if `loadConfig()` hasn't resolved yet. */
export function getConfig(): RuntimeConfig {
  if (!_config) throw new Error('config not loaded; await loadConfig() first');
  return _config;
}

/** Test/reset hook. */
export function __resetConfigForTests(next: RuntimeConfig | null = null): void {
  _config = next;
}

/**
 * Returns base URLs per service. Local dev hits services directly on
 * 8001/8002/8003 (cross-origin to expose CORS bugs); hosted uses the
 * single api.* origin and lets Caddy fan out by path.
 */
export function serviceBaseUrls(cfg: RuntimeConfig): {
  catalog: string;
  cart: string;
  checkout: string;
  instructor: string;
} {
  if (cfg.deployEnv === 'local') {
    const root = cfg.apiBaseUrl.replace(/\/$/, '');
    return {
      catalog: `${root}:8001`,
      cart: `${root}:8002`,
      checkout: `${root}:8003`,
      instructor: `${root}:8001`,
    };
  }
  const root = cfg.apiBaseUrl.replace(/\/$/, '');
  return {
    catalog: root,
    cart: root,
    checkout: root,
    instructor: root,
  };
}
